import { useRef, useState } from "react";
import { Upload, Trash2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, FormError, SubmitButton } from "@/components/common";
import { GarageAvatar } from "@/components/pro/GarageAvatar";
import { SlugStatus } from "@/pages/admin/AdminGarages";
import { useGarage } from "@/context/GarageContext";
import { api, errMsg } from "@/lib/api";
import { AU_STATES, TIMEZONES, CURRENCIES, DAYS, portalUrl, fmtYmd } from "@/lib/format";
import { t } from "@/i18n";

function useSave(path, onSaved) {
  const { setGarage } = useGarage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const save = async (payload) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.put(path, payload);
      setGarage(data);
      toast.success(t("common.saved"));
      onSaved?.(data);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, save };
}

function Sel({ value, onChange, options, testId, render = (o) => o }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={t("common.select")} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{render(o)}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function LogoUploader() {
  const { garage, setGarage, readOnly } = useGarage();
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/garage/logo", fd);
      setGarage(data);
      toast.success(t("garage.logoUploaded"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      ref.current.value = "";
    }
  };
  const remove = async () => {
    try {
      const { data } = await api.delete("/garage/logo");
      setGarage(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-4" data-testid="logo-uploader">
      <GarageAvatar garage={garage} size="lg" />
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="logo-file-input" />
      <Button type="button" variant="outline" disabled={busy || readOnly} onClick={() => ref.current.click()} data-testid="logo-upload-button">
        <Upload className="mr-2 h-4 w-4" /> {busy ? t("common.uploading") : t("garage.uploadLogo")}
      </Button>
      {garage.logo_url && (
        <Button type="button" variant="ghost" disabled={readOnly} onClick={remove} className="text-red-600" data-testid="logo-remove-button"><Trash2 className="mr-2 h-4 w-4" /> {t("common.remove")}</Button>
      )}
      <p className="w-full text-xs text-slate-500">{t("garage.logoHint")}</p>
    </div>
  );
}

export function ProfileForm({ submitLabel, onSaved }) {
  const { garage, readOnly } = useGarage();
  const c = garage.contact || {};
  const [f, setF] = useState({ name: garage.name, primary_color: garage.primary_color, timezone: garage.timezone, currency: garage.currency,
    address: c.address, suburb: c.suburb, state: c.state, postcode: c.postcode, phone: c.phone, email: c.email, abn: c.abn, gst_registered: !!c.gst_registered });
  const { busy, error, save } = useSave("/garage/profile", onSaved);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={(e) => { e.preventDefault(); save({ ...f, email: f.email || null }); }} className="space-y-5" data-testid="garage-profile-form">
      <FormError message={error} />
      <LogoUploader />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("garage.name")} htmlFor="gp-name"><Input id="gp-name" required value={f.name} onChange={set("name")} data-testid="garage-name-input" /></Field>
        <Field label={t("garage.primaryColour")} htmlFor="gp-color" hint={t("garage.colourHint")}>
          <div className="flex gap-2">
            <input type="color" value={f.primary_color} onChange={set("primary_color")} className="h-10 w-14 cursor-pointer rounded-md border border-input bg-white p-1" data-testid="garage-colour-picker" aria-label={t("garage.primaryColour")} />
            <Input id="gp-color" value={f.primary_color} onChange={set("primary_color")} data-testid="garage-colour-input" />
          </div>
        </Field>
        <Field label={t("garage.address")} htmlFor="gp-addr" className="sm:col-span-2"><Input id="gp-addr" value={f.address} onChange={set("address")} data-testid="garage-address-input" /></Field>
        <Field label={t("garage.suburb")} htmlFor="gp-sub"><Input id="gp-sub" value={f.suburb} onChange={set("suburb")} data-testid="garage-suburb-input" /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("garage.state")}><Sel value={f.state} onChange={(v) => setF({ ...f, state: v })} options={AU_STATES} testId="garage-state-select" /></Field>
          <Field label={t("garage.postcode")} htmlFor="gp-pc"><Input id="gp-pc" value={f.postcode} onChange={set("postcode")} data-testid="garage-postcode-input" /></Field>
        </div>
        <Field label={t("garage.phone")} htmlFor="gp-phone"><Input id="gp-phone" value={f.phone} onChange={set("phone")} data-testid="garage-phone-input" /></Field>
        <Field label={t("garage.email")} htmlFor="gp-email"><Input id="gp-email" type="email" value={f.email} onChange={set("email")} data-testid="garage-email-input" /></Field>
        <Field label={t("garage.abn")} htmlFor="gp-abn" hint={t("garage.abnHint")}><Input id="gp-abn" value={f.abn} onChange={set("abn")} data-testid="garage-abn-input" /></Field>
        <Field label={t("garage.gstRegistered")}>
          <div className="flex h-10 items-center gap-3">
            <Switch checked={f.gst_registered} onCheckedChange={(v) => setF({ ...f, gst_registered: v })} data-testid="garage-gst-switch" />
            <span className="text-sm text-slate-700">{f.gst_registered ? t("common.yes") : t("common.no")}</span>
          </div>
        </Field>
        <Field label={t("garage.timezone")}><Sel value={f.timezone} onChange={(v) => setF({ ...f, timezone: v })} options={TIMEZONES} testId="garage-timezone-select" /></Field>
        <Field label={t("garage.currency")}><Sel value={f.currency} onChange={(v) => setF({ ...f, currency: v })} options={CURRENCIES} testId="garage-currency-select" /></Field>
      </div>
      <SubmitButton busy={busy} disabled={readOnly} data-testid="garage-profile-save-button">{submitLabel || t("common.save")}</SubmitButton>
    </form>
  );
}

export function SlugChangeConfirm({ open, from, to, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent data-testid="slug-change-confirm">
        <DialogHeader><DialogTitle>{t("b11.slug.confirmTitle")}</DialogTitle><DialogDescription>{t("b11.slug.confirmText", { from: portalUrl(from), to: portalUrl(to) })}</DialogDescription></DialogHeader>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>{t("common.cancel")}</Button>
          <Button type="button" onClick={onConfirm} data-testid="slug-change-confirm-button">{t("b11.slug.confirm")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SlugForm({ submitLabel, onSaved, withName }) {
  const { garage, readOnly } = useGarage();
  const [slug, setSlug] = useState(garage.slug);
  const [name, setName] = useState(garage.name);
  const { busy, error, save } = useSave("/garage/slug", onSaved);
  const [confirm, setConfirm] = useState(false);
  const doSave = () => { setConfirm(false); save({ slug, name: withName ? name : undefined }); };
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (!withName && slug !== garage.slug) setConfirm(true); else doSave(); }} className="space-y-5" data-testid="garage-slug-form">
      <SlugChangeConfirm open={confirm} from={garage.slug} to={slug} onCancel={() => setConfirm(false)} onConfirm={doSave} />
      <FormError message={error} />
      {withName && <Field label={t("garage.name")} htmlFor="gs-name"><Input id="gs-name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="slug-form-name-input" /></Field>}
      <Field label={t("slug.label")} htmlFor="gs-slug" hint={portalUrl(slug || "…")}>
        <Input id="gs-slug" required value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} data-testid="garage-slug-input" />
        {slug !== garage.slug && <SlugStatus slug={slug} path="/garage/slug-available" />}
      </Field>
      <p className="text-xs text-slate-500">{t("slug.warning")}</p>
      <SubmitButton busy={busy} disabled={readOnly} data-testid="garage-slug-save-button">{submitLabel || t("common.save")}</SubmitButton>
    </form>
  );
}

export function BillingForm({ submitLabel, onSaved }) {
  const { garage, readOnly } = useGarage();
  const [f, setF] = useState({ currency: garage.currency, ...garage.billing });
  const { busy, error, save } = useSave("/garage/billing", onSaved);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const num = (k) => ({ type: "number", min: 1, required: true, value: f[k], onChange: set(k), "data-testid": `billing-${k.replace("_", "-")}-input` });
  const submit = (e) => {
    e.preventDefault();
    save({ ...f, gst_rate: Number(f.gst_rate), quote_next: Number(f.quote_next), invoice_next: Number(f.invoice_next), job_next: Number(f.job_next),
      credit_next: Number(f.credit_next || 1), payment_terms_days: Number(f.payment_terms_days ?? 14) });
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="billing-form">
      <FormError message={error} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("garage.currency")}><Sel value={f.currency} onChange={(v) => setF({ ...f, currency: v })} options={CURRENCIES} testId="billing-currency-select" /></Field>
        <Field label={t("billing.gstRate")} htmlFor="b-gst"><Input id="b-gst" type="number" step="0.1" min={0} max={100} required value={f.gst_rate} onChange={set("gst_rate")} data-testid="billing-gst-rate-input" /></Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {["quote", "invoice", "credit", "job"].map((k) => (
          <div key={k} className="space-y-3 rounded-lg border border-slate-200 p-4">
            <p className="text-sm font-semibold text-slate-800">{t(`billing.${k}Numbering`)}</p>
            <Field label={t("billing.prefix")}><Input value={f[`${k}_prefix`]} onChange={set(`${k}_prefix`)} data-testid={`billing-${k}-prefix-input`} /></Field>
            <Field label={t("billing.nextNumber")}><Input {...num(`${k}_next`)} /></Field>
            <p className="text-xs text-slate-500">{t("billing.example")}: <span className="font-mono">{f[`${k}_prefix`]}{String(f[`${k}_next`]).padStart(4, "0")}</span></p>
          </div>
        ))}
      </div>
      <Field label={t("billing.termsDays")} htmlFor="b-days" hint={t("billing.termsDaysHint")} className="max-w-xs"><Input id="b-days" type="number" min={0} max={365} required value={f.payment_terms_days ?? 14} onChange={set("payment_terms_days")} data-testid="billing-payment-terms-days-input" /></Field>
      <div className="space-y-3 rounded-lg border border-slate-200 p-4" data-testid="billing-bank-section">
        <p className="text-sm font-semibold text-slate-800">{t("billing.bank")}</p>
        <p className="text-xs text-slate-500">{t("billing.bankHint")}</p>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label={t("billing.bankName")} htmlFor="b-bname"><Input id="b-bname" value={f.bank_account_name || ""} onChange={set("bank_account_name")} data-testid="billing-bank-name-input" /></Field>
          <Field label="BSB" htmlFor="b-bsb"><Input id="b-bsb" placeholder="000-000" value={f.bank_bsb || ""} onChange={set("bank_bsb")} data-testid="billing-bank-bsb-input" /></Field>
          <Field label={t("billing.bankNumber")} htmlFor="b-bnum"><Input id="b-bnum" inputMode="numeric" value={f.bank_account_number || ""} onChange={set("bank_account_number")} data-testid="billing-bank-number-input" /></Field>
        </div>
      </div>
      <Field label={t("billing.paymentTerms")} htmlFor="b-pay"><Textarea id="b-pay" value={f.payment_terms} onChange={set("payment_terms")} data-testid="billing-payment-terms-input" /></Field>
      <Field label={t("billing.cancellationTerms")} htmlFor="b-cancel"><Textarea id="b-cancel" value={f.cancellation_terms} onChange={set("cancellation_terms")} data-testid="billing-cancellation-terms-input" /></Field>
      <SubmitButton busy={busy} disabled={readOnly} data-testid="billing-save-button">{submitLabel || t("common.save")}</SubmitButton>
    </form>
  );
}

export function HoursForm({ submitLabel, onSaved }) {
  const { garage, readOnly } = useGarage();
  const [hours, setHours] = useState(garage.hours);
  const [closures, setClosures] = useState(garage.closures || []);
  const [nc, setNc] = useState({ date: "", reason: "" });
  const { busy, error, save } = useSave("/garage/hours", onSaved);
  const setDay = (d, patch) => setHours({ ...hours, [d]: { ...hours[d], ...patch } });
  return (
    <form onSubmit={(e) => { e.preventDefault(); save({ hours, closures }); }} className="space-y-6" data-testid="hours-form">
      <FormError message={error} />
      <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
        {DAYS.map((d) => (
          <div key={d} className="flex flex-wrap items-center gap-3 p-3" data-testid={`hours-row-${d}`}>
            <span className="w-24 text-sm font-semibold text-slate-800">{t(`days.${d}`)}</span>
            <Switch checked={hours[d].open} onCheckedChange={(v) => setDay(d, { open: v })} data-testid={`hours-${d}-open-switch`} />
            <span className="w-16 text-xs text-slate-500">{hours[d].open ? t("hours.open") : t("hours.closed")}</span>
            {hours[d].open && (
              <div className="flex items-center gap-2">
                <Input type="time" value={hours[d].start} onChange={(e) => setDay(d, { start: e.target.value })} className="w-32" data-testid={`hours-${d}-start-input`} />
                <span className="text-slate-400">–</span>
                <Input type="time" value={hours[d].end} onChange={(e) => setDay(d, { end: e.target.value })} className="w-32" data-testid={`hours-${d}-end-input`} />
              </div>
            )}
          </div>
        ))}
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold text-slate-800">{t("hours.closures")}</p>
        <div className="space-y-2" data-testid="closures-list">
          {closures.length === 0 && <p className="text-sm text-slate-500">{t("hours.noClosures")}</p>}
          {closures.map((c, i) => (
            <div key={`${c.date}-${i}`} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <span><strong>{fmtYmd(c.date)}</strong> {c.reason && `· ${c.reason}`}</span>
              <Button type="button" variant="ghost" size="icon" onClick={() => setClosures(closures.filter((_, j) => j !== i))} data-testid={`remove-closure-${i}`} aria-label={t("common.remove")}><X className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Input type="date" value={nc.date} onChange={(e) => setNc({ ...nc, date: e.target.value })} className="w-44" data-testid="closure-date-input" />
          <Input placeholder={t("hours.reason")} value={nc.reason} onChange={(e) => setNc({ ...nc, reason: e.target.value })} className="w-56" data-testid="closure-reason-input" />
          <Button type="button" variant="outline" disabled={!nc.date} onClick={() => { setClosures([...closures, nc]); setNc({ date: "", reason: "" }); }} data-testid="add-closure-button"><Plus className="mr-1 h-4 w-4" /> {t("hours.addClosure")}</Button>
        </div>
      </div>
      <SubmitButton busy={busy} disabled={readOnly} data-testid="hours-save-button">{submitLabel || t("common.save")}</SubmitButton>
    </form>
  );
}
